/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from "react";
import { FormattedNumber } from "react-intl";
import {
  DerivedModifierGroup,
  uniqueModifierKey,
} from "../../hooks/useModifierSelection";

export interface ModifierGroupSelectorProps {
  modifierGroups: DerivedModifierGroup[];
  selectedOptions: any[];
  modifierGroupIdsToBeHighlighted: number[];
  onModifierSelect: (modifierGroupId: number, modifier: any) => void;
  className?: string;
  highlightTrigger?: number;
}

const getInitialExpandedState = (
  modifierGroups: DerivedModifierGroup[]
): Record<number, boolean> => {
  return Object.fromEntries(
    modifierGroups
      .filter((mg) => mg.minimumChoices > 0)
      .map((mg) => [mg.id, true])
  );
};

const getChoiceLabel = (
  minimumChoices: number,
  maximumChoices: number
): string => {
  const parts: string[] = [];
  if (minimumChoices > 1) {
    parts.push(`min: ${minimumChoices}`);
  }
  if (maximumChoices > 0) {
    parts.push(`max: ${maximumChoices}`);
  }
  return parts.length > 0 ? ` (${parts.join(", ")})` : "";
};

const ModifierGroupSelector: React.FC<ModifierGroupSelectorProps> = ({
  modifierGroups,
  selectedOptions,
  onModifierSelect,
  modifierGroupIdsToBeHighlighted,
  className = "",
  highlightTrigger = 0,
}) => {
  const btnOrangeClass = "btn-orange-toggle";
  const btnWhiteClass = "btn-white";

  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>(
    () => getInitialExpandedState(modifierGroups)
  );

  const modifierRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    if (modifierGroupIdsToBeHighlighted.length > 0) {
      const firstHighlightedId = modifierGroupIdsToBeHighlighted[0];
      const element = modifierRefs.current[firstHighlightedId];

      if (element !== null) {
        // Scroll to the first unselected required modifier group
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [modifierGroupIdsToBeHighlighted, highlightTrigger]);

  const toggleGroup = (groupId: number) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const visibleModifierGroups = modifierGroups
    .filter(
      (mg) =>
        mg.visible && mg.modifiers.filter((m: any) => m?.visible).length > 0
    )
    .sort((mg1, mg2) => mg1.displayOrder - mg2.displayOrder);

  return (
    <div className={`modifier-group-selector ${className}`}>
      {visibleModifierGroups.map(
        ({ id, maximumChoices, minimumChoices, modifiers, name }) => {
          const isRequired = minimumChoices > 0;

          const shouldHighlight = modifierGroupIdsToBeHighlighted.includes(id);

          const isModifierSelected = (modifier: any) =>
            selectedOptions.some(
              (o) => uniqueModifierKey(o) === uniqueModifierKey(modifier)
            );

          // Sort modifiers by displayOrder
          const sortedModifiers = [...modifiers].sort(
            (m1: any, m2: any) =>
              (m1.displayOrder || 0) - (m2.displayOrder || 0)
          );

          const previewModifiers = sortedModifiers.filter(
            (modifier: any, index: number) =>
              index < 3 || isModifierSelected(modifier)
          );

          const isExpanded = isRequired || expandedGroups[id];
          const visibleModifiers = isExpanded
            ? sortedModifiers
            : previewModifiers;

          const hasMore =
            !isRequired && modifiers.length > previewModifiers.length;

          const hiddenCount = modifiers.length - previewModifiers.length;

          return (
            <div
              className="modifier-container"
              key={id}
              ref={(el) => (modifierRefs.current[id] = el)}
            >
              <label
                key={
                  shouldHighlight ? `highlight-${highlightTrigger}` : "normal"
                }
                className={shouldHighlight ? "missing-required-selection" : ""}
              >
                {name}
                {isRequired ? "*" : ""}
                {getChoiceLabel(minimumChoices, maximumChoices)}
              </label>

              <div className="modifier-buttons">
                {visibleModifiers.map((modifier: any) => (
                  <button
                    type="button"
                    key={modifier.id}
                    className={`btn-default btn-modifier ${
                      isModifierSelected(modifier)
                        ? btnOrangeClass
                        : btnWhiteClass
                    }`}
                    onClick={() => onModifierSelect(id, modifier)}
                  >
                    {modifier.name + " "}
                    {modifier.price.amount > 0 ? (
                      <span>
                        {modifier.price.currencySymbol}
                        <FormattedNumber
                          minimumFractionDigits={2}
                          value={modifier.price.amount}
                        />
                      </span>
                    ) : null}
                  </button>
                ))}

                {hasMore && (
                  <button
                    type="button"
                    className="btn-default btn-modifier btn-white btn-toggle-more"
                    onClick={() => toggleGroup(id)}
                  >
                    {isExpanded ? "LESS ▲" : `SHOW ${hiddenCount} MORE ▼`}
                  </button>
                )}
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

export default ModifierGroupSelector;
